import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Checkbox,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
} from '@mui/material';
import FilterDropdown from '../components/common/FilterDropdown';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import { useUrlParam, useSetUrlParams } from '../components/common/useUrlParam';
import { useToast } from '../components/common/useToast';

import {
    Add,
    Category,
    Refresh,
    Search,
    Upload,
    Download,
    FiberManualRecord,
    WbTwilight,
    Close,
} from '@mui/icons-material';
import { WarehouseAPI } from '../services/api';
import { BundleSearchResponse, BundleSearchResult, BundleCategory } from '../types';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CreateBundleCategory from '../components/bundle-category/CreateBundleCategory';
import BundleCategoryCard from '../components/bundle-category/BundleCategoryCard';
import { SaleTagValue, normalizeSaleTagValue } from '../components/bundle-category/SaleTagToggle';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import BundleCategoryDetail from '../components/bundle-category/BundleCategoryDetail';
import { usePermission } from '../hooks/usePermission';
import CogsRangeFilter, { cogsRangeParams, isCogsRangeActive } from '../components/common/CogsRangeFilter';
import { cogsFilterFromParams, cogsFilterToParams } from './bundleCategoryCogsUrl';
import { csvCell } from '../utils/csv';


interface BundleCategoryManagerProps {
    embedded?: boolean;
}

export interface BundleCategoryManagerHandle {
    openCreateDialog: () => void;
    openBulkUploadDialog: () => void;
    exportData: () => void;
    refresh: () => void;
}

const BundleCategoryManager = forwardRef<BundleCategoryManagerHandle, BundleCategoryManagerProps>(({ embedded = false }, ref) => {
    // RBAC: all bundle-SKU mutate affordances (sale-tag edits, bulk upload, create
    // category) are gated on the bundle-skus matrix capability. The backend enforces the
    // same guard independently (defense in depth, SI-5).
    const canEdit = usePermission('bundle-skus').can('edit');
    // Creating a new bundle category is gated on the add-bundle capability (not
    // bundle-skus), so catalog roles that hold add-bundle:edit can create categories
    // while still using bundle-skus:edit for sale-tag / bulk-upload affordances. The
    // backend enforces add-bundle:edit on POST /api/bundle-categories independently.
    const canAddBundle = usePermission('add-bundle').can('edit');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [categories, setCategories] = useState<BundleCategory[]>([]);
    // Mirror of `categories` so async handlers (e.g. the Sale Tag Undo toast action, whose
    // onClick is captured once at fire time) always read the latest tags, not a stale closure.
    const categoriesRef = React.useRef(categories);
    categoriesRef.current = categories;
    const [selectedParents, setSelectedParents] = useState<Set<string>>(new Set());
    const [saleTagPending, setSaleTagPending] = useState<Set<string>>(new Set());
    const [undoSaleTag, setUndoSaleTag] = useState<{ message: string; previous: Record<string, SaleTagValue> } | null>(null);
    const reduceMotion = useReducedMotion();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const toast = useToast();

    // Edit dialog state
    const [editingCategory, setEditingCategory] = useState<BundleCategory | null>(null);
    const [editName, setEditName] = useState('');
    const [editImageURL, setEditImageURL] = useState('');
    const [editCategoryCode, setEditCategoryCode] = useState('');
    const [editBundleName, setEditBundleName] = useState('');
    const [editType, setEditType] = useState('');
    const [editMaxRetailPrice, setEditMaxRetailPrice] = useState('');
    const [editBasePrice, setEditBasePrice] = useState('');
    const [editBrand, setEditBrand] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editSubCategory, setEditSubCategory] = useState('');
    const [editHandle, setEditHandle] = useState('');
    const [editTagInput, setEditTagInput] = useState('');
    const [editTags, setEditTags] = useState<string[]>([]);
    const [editPlytixId, setEditPlytixId] = useState('');
    const [editCostPrice, setEditCostPrice] = useState('');
    const [editGstTaxTypeCode, setEditGstTaxTypeCode] = useState('3');
    const [editSaleType, setEditSaleType] = useState('Bundle');
    const [editLoading, setEditLoading] = useState(false);

    // Bulk upload state
    const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
    const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
    const [advancedUploadMode, setAdvancedUploadMode] = useState(false);
    const [advancedSyncMode, setAdvancedSyncMode] = useState(false);
    const [bulkUploadResults, setBulkUploadResults] = useState<{
        created?: Array<{ parentSKU: string; bundleSku: string }>;
        newVersions?: Array<{ parentSKU: string; bundleSku: string; version: number }>;
        inserted?: Array<{ row: number; parentSKU: string; bundleSku: string; versionNumber: number; isActive: boolean }>;
        skipped?: Array<{ row: number; bundleSku: string; reason: string }>;
        errors: Array<{ row: number; parentSKU?: string; bundleSku?: string; error: string }>;
    } | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const advancedFileInputRef = React.useRef<HTMLInputElement>(null);

    // Inventory state - map of bundleSku to total inventory
    const [inventoryMap, setInventoryMap] = useState<Record<string, number>>({});
    // Can be made state - map of bundleSku to maximum possible bundles
    const [canBeMadeMap, setCanBeMadeMap] = useState<Record<string, number>>({});

    // Search / sort / filters / pagination all live in the URL (prefix "bc") so a refresh restores
    // exactly where the user left off. Defaults stay OUT of the URL (note the non-'all' defaults
    // 'active' and 'Live' are serialized only when changed away from). Any filter/search/sort
    // change resets bcpage in the SAME update via the helper below. selectedParents/selectedCategory
    // are selection UI, NOT filters, so they stay in useState.
    const setUrlParams = useSetUrlParams();
    const [searchQuery] = useUrlParam('bcq', '');
    const [sortBy] = useUrlParam('bcsort', 'name-asc');
    const [statusFilter] = useUrlParam('bcstatus', 'active');
    const [brandFilter] = useUrlParam('bcbrand', 'all');
    const [categoryFilter] = useUrlParam('bccat', 'all');
    const [subCategoryFilter] = useUrlParam('bcsubcat', 'all');
    const [tagFilter] = useUrlParam('bctag', 'all');
    const [saleTypeFilter] = useUrlParam('bcsaletype', 'all');
    const [saleTagFilter] = useUrlParam('bcsaletag', 'Live');

    // COGS range filter — serialized across four bc-prefixed params so a refresh restores it.
    // Reconstructed into a CogsRangeValue for the shared <CogsRangeFilter>; defaults drop out of
    // the URL (see bundleCategoryCogsUrl helpers).
    const [cogsMinParam] = useUrlParam('bccogsmin', '');
    const [cogsMaxParam] = useUrlParam('bccogsmax', '');
    const [cogsNoneParam] = useUrlParam('bccogsnone', 'false');
    const [cogsPartialParam] = useUrlParam('bccogspartial', 'false');
    const cogsFilterValue = useMemo(
        () => cogsFilterFromParams({ min: cogsMinParam, max: cogsMaxParam, none: cogsNoneParam, partial: cogsPartialParam }),
        [cogsMinParam, cogsMaxParam, cogsNoneParam, cogsPartialParam],
    );

    const [pageParam, setPageParam] = useUrlParam('bcpage', '0');
    const page = parseInt(pageParam, 10) || 0;
    const setPage = (v: number) => setPageParam(v === 0 ? '0' : String(v));
    const [pageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);

    // Set one filter/search/sort key (dropped when it equals the given default) and reset the page
    // to 0 in the same URL update, so a filter change never leaves the user stranded on a now-empty
    // high page number.
    const setFilterParam = useCallback((key: string, value: string, def: string) => {
        setUrlParams({ [key]: value === def ? null : value, bcpage: null });
    }, [setUrlParams]);

    const setSearchQuery = (v: string) => setFilterParam('bcq', v, '');
    const setSortBy = (v: string) => setFilterParam('bcsort', v, 'name-asc');
    const setStatusFilter = (v: string) => setFilterParam('bcstatus', v, 'active');
    const setBrandFilter = (v: string) => setFilterParam('bcbrand', v, 'all');
    const setCategoryFilter = (v: string) => setFilterParam('bccat', v, 'all');
    const setSubCategoryFilter = (v: string) => setFilterParam('bcsubcat', v, 'all');
    const setTagFilter = (v: string) => setFilterParam('bctag', v, 'all');
    const setSaleTypeFilter = (v: string) => setFilterParam('bcsaletype', v, 'all');
    const setSaleTagFilter = (v: string) => setFilterParam('bcsaletag', v, 'Live');
    // Write all four bccogs* params in one update (defaults dropped) and reset the page, same as
    // setFilterParam does for the single-key filters.
    const setCogsFilter = (v: typeof cogsFilterValue) => setUrlParams({ ...cogsFilterToParams(v), bcpage: null });

    // "Clear All" sets every filter to 'all' in ONE URL update (back-to-back single-key setters
    // would clobber each other since each starts from the same snapshot). status/saleTag become
    // 'all' — which differs from their 'active'/'Live' defaults, so those keys are written, not
    // dropped; the rest default to 'all' and are removed. Page resets too.
    const clearAllFilters = () => {
        setUrlParams({
            bcstatus: 'all',
            bcbrand: null,
            bccat: null,
            bcsubcat: null,
            bctag: null,
            bcsaletype: null,
            bcsaletag: 'all',
            bccogsmin: null,
            bccogsmax: null,
            bccogsnone: null,
            bccogspartial: null,
            bcpage: null,
        });
    };

    const activeFilterCount = [statusFilter, brandFilter, categoryFilter, subCategoryFilter, tagFilter, saleTypeFilter, saleTagFilter]
        .filter(f => f !== 'all').length
        + (isCogsRangeActive(cogsFilterValue) ? 1 : 0);

    // Filter dropdown options — fetched once from the DB (distinct across ALL documents,
    // not just the currently loaded page) so options don't shrink/change as filters/pages change.
    const [filterOptions, setFilterOptions] = useState<{
        brands: string[];
        categories: string[];
        subCategories: string[];
        tags: string[];
    }>({ brands: [], categories: [], subCategories: [], tags: [] });

    useEffect(() => {
        WarehouseAPI.getBundleCategoryFilterValues().then(setFilterOptions);
    }, []);

    // Use categories directly as they are now filtered and sorted by backend
    const filteredAndSortedCategories = categories;

    const applySaleTagLocally = useCallback((tags: Record<string, SaleTagValue>) => {
        setCategories(prev => prev.map(c => {
            const tag = tags[c.parentSKU];
            if (!tag) return c;
            return {
                ...c,
                activeBundle: c.activeBundle ? { ...c.activeBundle, saleTag: tag } : c.activeBundle,
                latestInactiveVersion: c.latestInactiveVersion ? { ...c.latestInactiveVersion, saleTag: tag } : c.latestInactiveVersion,
            };
        }));
    }, []);

    // Sets the Sale Tag for whole parents. Updates the UI immediately, rolls back on failure,
    // and offers Undo when every parent had a tag before (an unset one can't be restored).
    const changeSaleTag = async (parentSKUs: string[], next: SaleTagValue) => {
        const before: Record<string, SaleTagValue | undefined> = {};
        categoriesRef.current.forEach(c => {
            if (parentSKUs.includes(c.parentSKU)) {
                before[c.parentSKU] = normalizeSaleTagValue(c.activeBundle?.saleTag ?? c.latestInactiveVersion?.saleTag);
            }
        });
        const targets = parentSKUs.filter(p => before[p] !== next);
        if (targets.length === 0) return;

        setSaleTagPending(prev => new Set([...Array.from(prev), ...targets]));
        applySaleTagLocally(Object.fromEntries(targets.map(p => [p, next])));
        try {
            const result = await WarehouseAPI.setParentSaleTag(targets, next);
            const undoable = targets.every(p => before[p]);
            toast.success(
                targets.length === 1
                    ? `${targets[0]} is now ${next} (${result.updatedVersions} version${result.updatedVersions === 1 ? '' : 's'})`
                    : `${result.updatedParents} parents set to ${next} (${result.updatedVersions} versions)`,
                { duration: 4000 },
            );
            const undoState = undoable
                ? { message: '', previous: Object.fromEntries(targets.map(p => [p, before[p] as SaleTagValue])) }
                : null;
            setUndoSaleTag(undoState);
            if (undoState) {
                toast('Sale Tag updated', {
                    duration: 8000,
                    action: { label: 'Undo', onClick: () => handleUndoSaleTag(undoState) },
                });
            }
            setSelectedParents(new Set());
        } catch (err: any) {
            applySaleTagLocally(Object.fromEntries(targets.filter(p => before[p]).map(p => [p, before[p] as SaleTagValue])));
            if (targets.some(p => !before[p])) await fetchCategories();
            setUndoSaleTag(null);
            toast.error(err.message || 'Failed to update Sale Tag', { duration: 4000 });
        } finally {
            setSaleTagPending(prev => {
                const nextSet = new Set(prev);
                targets.forEach(p => nextSet.delete(p));
                return nextSet;
            });
        }
    };

    const handleUndoSaleTag = async (snapshot?: { message: string; previous: Record<string, SaleTagValue> } | null) => {
        const state = snapshot ?? undoSaleTag;
        if (!state) return;
        const { previous } = state;
        setUndoSaleTag(null);
        for (const tag of ['Live', 'Sunset'] as SaleTagValue[]) {
            const group = Object.keys(previous).filter(p => previous[p] === tag);
            if (group.length > 0) await changeSaleTag(group, tag);
        }
        setUndoSaleTag(null);
    };

    const toggleParentSelected = (parentSKU: string, on: boolean) => {
        setSelectedParents(prev => {
            const next = new Set(prev);
            if (on) next.add(parentSKU); else next.delete(parentSKU);
            return next;
        });
    };

    const canEditSaleTag = canEdit;
    const pageParentSKUs = categories.map(c => c.parentSKU);
    const selectedOnPage = pageParentSKUs.filter(p => selectedParents.has(p)).length;


    const fetchCategories = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const filters = {
                statusFilter,
                brandFilter,
                categoryFilter,
                subCategoryFilter,
                tagFilter,
                saleTypeFilter,
                saleTagFilter,
                // COGS range params (cogsMin/cogsMax/cogsNone/cogsPartialOnly) spread in as-is —
                // getBundleCategories forwards the filters object straight into the query string,
                // and the route reads these exact keys from req.query.
                ...cogsRangeParams(cogsFilterValue),
            };
            const response = await WarehouseAPI.getBundleCategories(page, pageSize, searchQuery, sortBy, filters);
            // Cast to intermediate any or adjust type if needed, but the backend structure
            // matches BundleCategory properly now.
            const cats = response.data as any[];
            setCategories(cats);
            setTotalCount(response.total);

            // Populate inventory and canBeMade maps instantly from backend pre-fetched data
            const inventoryData: Record<string, number> = {};
            const canBeMadeData: Record<string, number> = {};
            cats.forEach(cat => {
                if (cat.activeBundle) {
                    inventoryData[cat.activeBundle.bundleSku] = cat.activeBundle.inventory || 0;
                    canBeMadeData[cat.activeBundle.bundleSku] = cat.activeBundle.canBeMade || 0;
                }
            });
            setInventoryMap(inventoryData);
            setCanBeMadeMap(canBeMadeData);

            return response.data;
        } catch (err: any) {
            setError(err.message || 'Failed to fetch bundle categories');
            return [];
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, searchQuery, sortBy, statusFilter, brandFilter, categoryFilter, subCategoryFilter, tagFilter, saleTypeFilter, saleTagFilter, cogsFilterValue]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Read parentSKU from URL search params -> show detail view (used by BundleToolTables row click)
    useEffect(() => {
        const parentSKU = searchParams.get("parentSKU");
        if (parentSKU) {
            setSelectedCategory(parentSKU);
        }
    }, [searchParams]);
   


    const handleCreateSuccess = () => {
        setShowCreateDialog(false);
        fetchCategories();
        toast.success('Bundle category created successfully!', { duration: 4000 });
    };

    const handleCategoryClick = (parentSKU: string) => {
        setSelectedCategory(parentSKU);
    };

    const handleCloseDetail = () => {
        setSelectedCategory(null);
        fetchCategories();
        navigate('/bundle-categories');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const handleEditCategory = (e: React.MouseEvent, category: BundleCategory) => {
        e.stopPropagation(); // Prevent card click
        const displayData = category.activeBundle || category.latestInactiveVersion;

        setEditingCategory(category);
        setEditName(category.parentSKU);
        setEditImageURL(displayData?.imageURL || '');
        setEditCategoryCode(displayData?.categoryCode || '');
        setEditBundleName(displayData?.name || '');
        setEditType(displayData?.type || '');
        setEditMaxRetailPrice(displayData?.maxRetailPrice?.toString() || '');
        setEditBasePrice(displayData?.basePrice?.toString() || '');
        setEditBrand(displayData?.brand || '');
        setEditCategory(displayData?.category || '');
        setEditSubCategory(displayData?.subCategory || '');
        setEditHandle(displayData?.handle || '');
        setEditTags(displayData?.tags || []);
        setEditPlytixId(displayData?.plytix_id || '');
        setEditCostPrice(displayData?.costPrice?.toString() || '');
        setEditGstTaxTypeCode(displayData?.gstTaxTypeCode || '3');
        setEditSaleType(displayData?.saleType || 'Bundle');
        setEditTagInput('');
    };

    const handleAddEditTag = () => {
        const trimmed = editTagInput.trim();
        if (trimmed && !editTags.includes(trimmed)) {
            setEditTags([...editTags, trimmed]);
            setEditTagInput('');
        }
    };

    const handleRemoveEditTag = (index: number) => {
        setEditTags(editTags.filter((_, i) => i !== index));
    };

    const handleEditTagKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEditTag();
        }
    };

    const handleSaveEdit = async () => {
        if (!editingCategory) return;

        setEditLoading(true);
        try {
            await WarehouseAPI.updateBundleCategory(editingCategory.parentSKU, {
                newParentSKU: editName.trim() !== editingCategory.parentSKU ? editName.trim() : undefined,
                imageURL: editImageURL.trim() || null,
                categoryCode: editCategoryCode.trim() || null,
                name: editBundleName.trim() || null,
                type: editType.trim() || null,
                maxRetailPrice: editMaxRetailPrice ? parseFloat(editMaxRetailPrice) : null,
                basePrice: editBasePrice ? parseFloat(editBasePrice) : null,
                brand: editBrand.trim() || null,
                category: editCategory.trim() || null,
                subCategory: editSubCategory.trim() || null,
                handle: editHandle.trim() || null,
                tags: editTags,
                plytix_id: editPlytixId.trim() || null,
                costPrice: editCostPrice ? parseFloat(editCostPrice) : null,
                gstTaxTypeCode: editGstTaxTypeCode.trim() || "3",
                saleType: editSaleType,
            });
            await fetchCategories();
            toast.success('Bundle updated successfully!', { duration: 4000 });
            setEditingCategory(null);
        } catch (err: any) {
            toast.error(err.message || 'Failed to update bundle', { duration: 4000 });
        } finally {
            setEditLoading(false);
        }
    };

    const handleExportData = async () => {
        setLoading(true);
        try {
            // Fetch all bundle versions (SKUs) using search API with includeInactive=true
            // Using a large limit to get all records
            // includeCogs=true so each returned version carries its post-GST COGS summary
            // (same source/row model as before — one row per bundle version).
            const result: BundleSearchResponse = await WarehouseAPI.searchBundleCategories('', 0, 5000, true, true);
            const allBundles: BundleSearchResult[] = result.data || [];

            if (allBundles.length === 0) {
                toast.error('No data to export', { duration: 4000 });
                return;
            }

            // Plain numbers (no thousands separators, no dashes) so the COGS columns open as
            // numbers in Excel/Sheets — same n() discipline as the Simple/Bundle export.
            const n = (v: unknown): string | number => {
                if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '';
                return Number(v);
            };

            // Prepare CSV data
            const csvRows = [];

            // CSV Header
            csvRows.push([
                'Parent SKU',
                'Bundle SKU',
                'Status',
                'Version',
                'Category Code',
                'Name',
                'Type',
                'MRP',
                'Base Price',
                'Brand',
                'Category',
                'Sub Category',
                'Handle',
                'Child SKUs',
                'Tags',
                'Plytix ID',
                'Image URL',
                'Inventory',
                'Can Be Made',
                'DRR',
                'DOC',
                'Created At',
                'COGS (post-GST)',
                'Components Costed',
                'Components Total'
            ].join(','));

            // Add data rows
            allBundles.forEach(bundle => {
                const row = [
                    bundle.parentSKU,
                    bundle.bundleSku,
                    bundle.isActive ? 'ACTIVE' : 'INACTIVE',
                    bundle.versionNumber,
                    bundle.categoryCode || '',
                    bundle.name || '',
                    bundle.type || '',
                    bundle.maxRetailPrice || '',
                    bundle.basePrice || '',
                    bundle.brand || '',
                    bundle.category || '',
                    bundle.subCategory || '',
                    bundle.handle || '',
                    (bundle.products || []).join(','),
                    (bundle.tags || []).join(','),
                    bundle.plytix_id || '',
                    bundle.imageURL || '',
                    bundle.inventory || 0,
                    bundle.canBeMade || 0,
                    // Blank when the DRR lookup missed — never a misleading 0.
                    bundle.drr ?? '',
                    // Real enriched physical DOC (matches the grid), blank when unknown — not a
                    // hand-rolled inventory/drr and never a misleading 0.
                    bundle.physicalDoc ?? '',
                    new Date(bundle.createdAt).toLocaleString('en-IN'),
                    // COGS columns — plain numbers (blank when null), NOT quoted so Excel reads
                    // them as numbers. Matches the Simple/Bundle plain export naming/values.
                    n(bundle.cogs?.total),
                    n(bundle.cogs?.costedCount),
                    n(bundle.cogs?.componentCount)
                ];
                csvRows.push(row.map(csvCell).join(','));
            });

            // Create and download CSV. Prepend the UTF-8 BOM so Excel reads it as UTF-8
            // (matches the Simple/Bundle export's downloadCsv).
            const csvContent = csvRows.join('\n');
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `all-bundle-skus-export-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('All SKUs exported successfully!', { duration: 4000 });

            // Log the export action
            await WarehouseAPI.logBundleCategoryAction('bundle-export', {
                itemCount: allBundles.length,
                timestamp: new Date().toISOString(),
                fileName: `all-bundle-skus-export-${new Date().toISOString().split('T')[0]}.csv`
            });
        } catch (err: any) {
            console.error('Export failed:', err);
            toast.error('Export failed: ' + err.message, { duration: 4000 });
        } finally {
            setLoading(false);
        }
    };

    // Exposes this tab's actions to the shared header buttons in BundleToolTables,
    // since Export/Bulk Upload/New Category/Refresh now live in that outer header
    // (matching GB/Bundle View's layout) instead of this component's own header.
    useImperativeHandle(ref, () => ({
        openCreateDialog: () => setShowCreateDialog(true),
        openBulkUploadDialog: () => setShowBulkUploadDialog(true),
        exportData: handleExportData,
        refresh: () => fetchCategories(),
    }));

    const handleDownloadTemplate = () => {
        const csvContent = `parentSKU,childs,categoryCode,name,type,maxRetailPrice,basePrice,brand,category,subCategory,handle,tags,imageURL,isFuture
EXAMPLE-BUNDLE,"CHILD1,CHILD2,CHILD3",CAT001,Diwali Gift Box,GIFT,999.00,799.00,Salty,Gifts,Festival,diwali-gift-box,"tag1,tag2",https://example.com/image.jpg,false
ANOTHER-BUNDLE,"SKU-A,SKU-B",CAT002,Combo Pack,COMBO,599.00,449.00,Salty,Combos,Daily,combo-pack,,false
PRODUCT-BOX,"ITEM1,ITEM2,ITEM3,ITEM4",CAT003,Subscription Box,SUBSCRIPTION,1299.00,999.00,Salty,Subscriptions,Monthly,subscription-box,https://example.com/box.png,false
FUTURE-BOX,"SKU-X,SKU-Y",CAT004,Future Bundle,COMBO,799.00,699.00,Salty,Combos,Daily,future-box,,true`;

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bundle_categories_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setBulkUploadLoading(true);
        setBulkUploadResults(null);

        try {
            const text = await file.text();
            const lines = text.split('\n').filter(line => line.trim());

            if (lines.length < 2) {
                throw new Error('CSV file must have a header row and at least one data row');
            }

            // Parse CSV - skip header row
            const bundles: Array<{
                parentSKU: string;
                childs: string[];
                isFuture?: boolean;
                categoryCode?: string;
                name?: string;
                type?: string;
                maxRetailPrice?: number;
                basePrice?: number;
                brand?: string;
                category?: string;
                subCategory?: string;
                handle?: string;
                tags?: string[];
                plytix_id?: string;
                imageURL?: string;
            }> = [];

            // Proper CSV parsing function that handles empty fields, quoted fields, and escaped quotes
            const parseCSVLine = (line: string): string[] => {
                const result: string[] = [];
                let current = '';
                let inQuotes = false;

                for (let j = 0; j < line.length; j++) {
                    const char = line[j];
                    const nextChar = line[j + 1];

                    if (inQuotes) {
                        if (char === '"' && nextChar === '"') {
                            current += '"';
                            j++;
                        } else if (char === '"') {
                            inQuotes = false;
                        } else {
                            current += char;
                        }
                    } else {
                        if (char === '"') {
                            inQuotes = true;
                        } else if (char === ',') {
                            result.push(current.trim());
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                }
                result.push(current.trim());
                return result;
            };

            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                // Use proper CSV parser
                const fields = parseCSVLine(line);
                if (fields.length < 2) continue;

                const parentSKU = fields[0] || '';
                const childsRaw = fields[1] || '';
                const categoryCode = fields[2] || '';
                const name = fields[3] || '';
                const type = fields[4] || '';
                const maxRetailPriceStr = fields[5] || '';
                const basePriceStr = fields[6] || '';
                const brand = fields[7] || '';
                const category = fields[8] || '';
                const subCategory = fields[9] || '';
                const handle = fields[10] || '';
                const tagsRaw = fields[11] || '';
                const plytixId = fields[12] || '';
                const imageURL = fields[13] || '';
                const isFutureStr = (fields[14] || '').toLowerCase();

                // Parse childs - support both comma-separated and JSON array format
                let childs: string[] = [];
                if (childsRaw) {
                    // Check if it's a JSON array format like ["SKU1","SKU2"]
                    if (childsRaw.trim().startsWith('[') && childsRaw.trim().endsWith(']')) {
                        try {
                            childs = JSON.parse(childsRaw).filter((c: string) => c);
                        } catch (e) {
                            // If JSON parsing fails, fall back to comma-separated
                            childs = childsRaw.split(',').map(c => c.trim()).filter(c => c);
                        }
                    } else {
                        // Standard comma-separated format
                        childs = childsRaw.split(',').map(c => c.trim()).filter(c => c);
                    }
                }

                if (parentSKU && childs.length > 0) {
                    bundles.push({
                        parentSKU,
                        childs,
                        categoryCode: categoryCode || undefined,
                        name: name || undefined,
                        type: type || undefined,
                        maxRetailPrice: maxRetailPriceStr ? parseFloat(maxRetailPriceStr) : undefined,
                        basePrice: basePriceStr ? parseFloat(basePriceStr) : undefined,
                        brand: brand || undefined,
                        category: category || undefined,
                        subCategory: subCategory || undefined,
                        handle: handle || undefined,
                        tags: tagsRaw ? tagsRaw.split(',').map(t => t.trim()).filter(t => t) : undefined,
                        plytix_id: plytixId || undefined,
                        imageURL: imageURL || undefined,
                        isFuture: isFutureStr === 'true' || isFutureStr === '1',
                    });
                }
            }

            if (bundles.length === 0) {
                throw new Error('No valid bundles found in CSV');
            }

            // Log bulk upload start (summary is logged on backend upon completion)
            await WarehouseAPI.logBundleCategoryAction('bundle-bulk-upload-start', {
                itemCount: bundles.length,
                mode: 'regular'
            });

            const result = await WarehouseAPI.bulkUploadBundleCategories(bundles);

            // Regular Upload's own copy claims to "sync with Unicommerce", but the backend
            // only ever wrote a log entry for bulk-created bundles — it never called the
            // create-item webhook. Manual single-bundle creation already does this sync
            // client-side (to avoid the EC2 server IP getting blocked by Unicommerce), so
            // mirror that here: for every bundle the backend just created, call the same
            // client-side webhook using the original CSV row's fields plus the bundleSku/
            // version the backend resolved (only known after the backend assigns it).
            const bundleByParentSKU = new Map(bundles.map(b => [b.parentSKU.toUpperCase().trim(), b]));
            const syncErrors: Array<{ row: number; parentSKU?: string; bundleSku?: string; error: string }> = [];
            const createdEntries = [
                ...(result.results.created || []).map(c => ({ ...c, versionNumber: 1 })),
                ...(result.results.newVersions || []).map(v => ({ ...v, versionNumber: v.version })),
            ];

            for (const entry of createdEntries) {
                const source = bundleByParentSKU.get(entry.parentSKU.toUpperCase().trim());
                if (!source) continue;
                try {
                    await WarehouseAPI.syncBundleToUnicommerceClientSide({
                        parentSKU: entry.parentSKU,
                        bundleSku: entry.bundleSku,
                        versionNumber: entry.versionNumber,
                        childs: source.childs,
                        categoryCode: source.categoryCode,
                        name: source.name,
                        type: source.type || 'BUNDLE',
                        maxRetailPrice: source.maxRetailPrice,
                        basePrice: source.basePrice,
                        imageURL: source.imageURL,
                    });
                    // Logged server-side (not just shown in this browser tab) so a failed
                    // sync is still diagnosable after the fact, even if no one saw the
                    // error banner at the time.
                    await WarehouseAPI.logBundleCategoryAction('bundle-unicommerce-sync-success', {
                        parentSKU: entry.parentSKU,
                        bundleSku: entry.bundleSku,
                        mode: 'regular',
                    });
                } catch (syncErr: any) {
                    syncErrors.push({
                        row: bundles.indexOf(source) + 2,
                        parentSKU: entry.parentSKU,
                        bundleSku: entry.bundleSku,
                        error: `Unicommerce sync failed: ${syncErr.message || syncErr}`,
                    });
                    await WarehouseAPI.logBundleCategoryAction('bundle-unicommerce-sync-failed', {
                        parentSKU: entry.parentSKU,
                        bundleSku: entry.bundleSku,
                        mode: 'regular',
                        error: syncErr.message || String(syncErr),
                    });
                }
            }

            setBulkUploadResults({
                ...result.results,
                errors: [...(result.results.errors || []), ...syncErrors],
            });
            await fetchCategories();

            const successCount = createdEntries.length;
            const syncedCount = successCount - syncErrors.length;
            const message = syncErrors.length > 0
                ? `Processed ${successCount} bundles — ${syncedCount} synced to Unicommerce, ${syncErrors.length} failed to sync (see errors below)`
                : `Successfully processed ${successCount} bundles and synced to Unicommerce!`;
            if (syncErrors.length > 0) {
                toast.error(message, { duration: 4000 });
            } else {
                toast.success(message, { duration: 4000 });
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to upload', { duration: 4000 });
        } finally {
            setBulkUploadLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleAdvancedFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setBulkUploadLoading(true);
        setBulkUploadResults(null);

        try {
            const text = await file.text();
            const lines = text.split('\n').filter(line => line.trim());

            if (lines.length < 2) {
                throw new Error('CSV file must have a header row and at least one data row');
            }

            // Parse CSV for advanced upload
            const bundles: Array<{
                parentSKU: string;
                bundleSku?: string;
                versionNumber?: number;
                childs: string[];
                isFuture?: boolean;
                isActive?: boolean;
                categoryCode?: string;
                name?: string;
                type?: string;
                maxRetailPrice?: number;
                basePrice?: number;
                brand?: string;
                category?: string;
                subCategory?: string;
                handle?: string;
                tags?: string[];
                plytix_id?: string;
                imageURL?: string;
            }> = [];

            // Proper CSV parsing function that handles empty fields, quoted fields, and escaped quotes
            const parseCSVLine = (line: string): string[] => {
                const result: string[] = [];
                let current = '';
                let inQuotes = false;

                for (let i = 0; i < line.length; i++) {
                    const char = line[i];
                    const nextChar = line[i + 1];

                    if (inQuotes) {
                        if (char === '"' && nextChar === '"') {
                            // Escaped quote
                            current += '"';
                            i++; // Skip next quote
                        } else if (char === '"') {
                            // End of quoted field
                            inQuotes = false;
                        } else {
                            current += char;
                        }
                    } else {
                        if (char === '"') {
                            // Start of quoted field
                            inQuotes = true;
                        } else if (char === ',') {
                            // End of field
                            result.push(current.trim());
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                }
                // Don't forget the last field
                result.push(current.trim());
                return result;
            };

            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                // Use proper CSV parser
                const fields = parseCSVLine(line);
                if (fields.length < 3) continue;

                const parentSKU = fields[0] || '';
                const bundleSku = fields[1] || '';
                const versionNumberStr = fields[2] || '';
                const childsRaw = fields[3] || '';
                const isActiveStr = (fields[4] || '').toLowerCase();
                const categoryCode = fields[5] || '';
                const name = fields[6] || '';
                const type = fields[7] || '';
                const maxRetailPriceStr = fields[8] || '';
                const basePriceStr = fields[9] || '';
                const brand = fields[10] || '';
                const category = fields[11] || '';
                const subCategory = fields[12] || '';
                const handle = fields[13] || '';
                const tagsRaw = fields[14] || '';
                const plytixId = fields[15] || '';
                const imageURL = fields[16] || '';
                const isFutureStr = (fields[17] || '').toLowerCase();

                // Parse childs - support both comma-separated and JSON array format
                let childs: string[] = [];
                if (childsRaw) {
                    // Check if it's a JSON array format like ["SKU1","SKU2"]
                    if (childsRaw.trim().startsWith('[') && childsRaw.trim().endsWith(']')) {
                        try {
                            childs = JSON.parse(childsRaw).filter((c: string) => c);
                        } catch (e) {
                            // If JSON parsing fails, fall back to comma-separated
                            childs = childsRaw.split(',').map(c => c.trim()).filter(c => c);
                        }
                    } else {
                        // Standard comma-separated format
                        childs = childsRaw.split(',').map(c => c.trim()).filter(c => c);
                    }
                }

                if (parentSKU && childs.length > 0) {
                    bundles.push({
                        parentSKU,
                        bundleSku: bundleSku || undefined,
                        versionNumber: versionNumberStr ? parseInt(versionNumberStr) : undefined,
                        childs,
                        isFuture: isFutureStr === 'true' || isFutureStr === '1',
                        isActive: isActiveStr === 'true' || isActiveStr === '1',
                        categoryCode: categoryCode || undefined,
                        name: name || undefined,
                        type: type || undefined,
                        maxRetailPrice: maxRetailPriceStr ? parseFloat(maxRetailPriceStr) : undefined,
                        basePrice: basePriceStr ? parseFloat(basePriceStr) : undefined,
                        brand: brand || undefined,
                        category: category || undefined,
                        subCategory: subCategory || undefined,
                        handle: handle || undefined,
                        tags: tagsRaw ? tagsRaw.split(',').map(t => t.trim()).filter(t => t) : undefined,
                        plytix_id: plytixId || undefined,
                        imageURL: imageURL || undefined,
                    });
                }
            }

            if (bundles.length === 0) {
                throw new Error('No valid bundles found in CSV');
            }

            // Log advanced bulk upload start
            await WarehouseAPI.logBundleCategoryAction('bundle-bulk-upload-start', {
                itemCount: bundles.length,
                mode: 'advanced',
                syncToExternal: advancedSyncMode
            });

            const result = await WarehouseAPI.bulkUploadBundleCategoriesRaw(bundles, advancedSyncMode);

            // "Advanced (Backfill)" intentionally stays DB-only (it's for records that
            // already exist in Unicommerce). "Advanced (Unicommerce)" (advancedSyncMode)
            // is meant to push new items to Unicommerce too, but the backend's syncToExternal
            // path only ever logged the action — same underlying gap as Regular Upload. Only
            // run the client-side sync when the user explicitly chose Unicommerce mode.
            const syncErrors: Array<{ row: number; parentSKU?: string; bundleSku?: string; error: string }> = [];
            if (advancedSyncMode) {
                for (const entry of (result.results.inserted || [])) {
                    const source = bundles[entry.row - 1];
                    if (!source) continue;
                    try {
                        await WarehouseAPI.syncBundleToUnicommerceClientSide({
                            parentSKU: entry.parentSKU,
                            bundleSku: entry.bundleSku,
                            versionNumber: entry.versionNumber,
                            childs: source.childs,
                            categoryCode: source.categoryCode,
                            name: source.name,
                            type: source.type || 'BUNDLE',
                            maxRetailPrice: source.maxRetailPrice,
                            basePrice: source.basePrice,
                            imageURL: source.imageURL,
                        });
                        // Logged server-side (not just shown in this browser tab) so a
                        // failed sync is still diagnosable after the fact, even if no one
                        // saw the error banner at the time.
                        await WarehouseAPI.logBundleCategoryAction('bundle-unicommerce-sync-success', {
                            parentSKU: entry.parentSKU,
                            bundleSku: entry.bundleSku,
                            mode: 'advanced',
                        });
                    } catch (syncErr: any) {
                        syncErrors.push({
                            row: entry.row,
                            parentSKU: entry.parentSKU,
                            bundleSku: entry.bundleSku,
                            error: `Unicommerce sync failed: ${syncErr.message || syncErr}`,
                        });
                        await WarehouseAPI.logBundleCategoryAction('bundle-unicommerce-sync-failed', {
                            parentSKU: entry.parentSKU,
                            bundleSku: entry.bundleSku,
                            mode: 'advanced',
                            error: syncErr.message || String(syncErr),
                        });
                    }
                }
            }

            setBulkUploadResults({
                ...result.results,
                errors: [...(result.results.errors || []), ...syncErrors],
            });
            await fetchCategories();

            const successCount = result.results.inserted?.length || 0;
            const syncedCount = successCount - syncErrors.length;
            const message = advancedSyncMode
                ? (syncErrors.length > 0
                    ? `Inserted ${successCount} bundles — ${syncedCount} synced to Unicommerce, ${syncErrors.length} failed to sync (see errors below)`
                    : `Inserted ${successCount} bundles and synced to Unicommerce, ${result.results.skipped?.length || 0} skipped!`)
                : `Inserted ${successCount} bundles, ${result.results.skipped?.length || 0} skipped!`;
            if (syncErrors.length > 0) {
                toast.error(message, { duration: 4000 });
            } else {
                toast.success(message, { duration: 4000 });
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to upload', { duration: 4000 });
        } finally {
            setBulkUploadLoading(false);
            if (advancedFileInputRef.current) {
                advancedFileInputRef.current.value = '';
            }
        }
    };

    const handleDownloadAdvancedTemplate = () => {
        const csvContent = `parentSKU,bundleSku,versionNumber,childs,isActive,categoryCode,name,type,maxRetailPrice,basePrice,brand,category,subCategory,handle,tags,imageURL,isFuture
TEST-BOX,TEST-BOX-1,1,"CHILD1,CHILD2",false,CAT001,Test Box v1,GIFT,999.00,799.00,Salty,Gifts,Festival,test-box-v1,"tag1,tag2",https://example.com/v1.jpg,false
TEST-BOX,TEST-BOX-2,2,"CHILD1,CHILD2,CHILD3",true,CAT001,Test Box v2,GIFT,1099.00,899.00,Salty,Gifts,Festival,test-box-v2,https://example.com/v2.jpg,false
COMBO-PACK,COMBO-PACK-1,1,"SKU-A,SKU-B",true,CAT002,Combo Pack,COMBO,599.00,449.00,Salty,Combos,Daily,combo-pack,,false
FUTURE-COMBO,FUTURE-COMBO-1,1,"SKU-X,SKU-Y",false,CAT003,Future Combo,COMBO,799.00,699.00,Salty,Combos,Daily,future-combo,,true`;

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bundle_categories_advanced_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (selectedCategory) {
        const detail = (
            <BundleCategoryDetail
                parentSKU={selectedCategory}
                onClose={handleCloseDetail}
                onUpdate={fetchCategories}
            />
        );
        // The detail view is now edge-to-edge (no self-imposed width cap); its gutter comes from an
        // enclosing PageContainer. Embedded inside BundleToolTables the enclosing PageContainer is
        // already present, so wrapping again would double the gutter — only wrap in the standalone
        // path, where Manager renders without one.
        return embedded ? detail : <PageContainer>{detail}</PageContainer>;
    }

    return (
        <Box sx={embedded ? { maxWidth: '100%', mx: 'auto', px: 0, py: 0 } : { px: { xs: 2, sm: 3, md: 4 }, pt: 2, pb: 3 }}>
            {/* Page-level header — hidden when embedded inside BundleToolTables, which
                renders its own title and drives Export/Bulk Upload/New Category/Refresh
                via the imperative handle above instead. */}
            {!embedded && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                    <PageHeader
                        icon={<Category />}
                        title="Bundle SKUs"
                        subtitle="Manage bundle SKUs and their child mappings"
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Tooltip title="Refresh Categories">
                            <IconButton
                                onClick={() => fetchCategories()}
                                color="primary"
                            >
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={handleExportData}
                            disabled={categories.length === 0}
                            sx={{
                                borderColor: '#16A34A',
                                color: '#16A34A',
                                '&:hover': {
                                    borderColor: '#15803D',
                                    bgcolor: 'rgba(22, 163, 74, 0.1)',
                                },
                                '&:disabled': {
                                    borderColor: '#DAD6CC',
                                    color: '#B8B2A6',
                                },
                            }}
                        >
                            Export
                        </Button>
                        {canEdit && (
                            <Button
                                variant="outlined"
                                startIcon={<Upload />}
                                onClick={() => setShowBulkUploadDialog(true)}
                                sx={{
                                    borderColor: '#DC5829',
                                    color: '#DC5829',
                                    '&:hover': {
                                        borderColor: '#F26A2E',
                                        bgcolor: 'rgba(220, 88, 41, 0.1)',
                                    },
                                }}
                            >
                                Bulk Upload
                            </Button>
                        )}
                        {canAddBundle && (
                            <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setShowCreateDialog(true)}
                            sx={{
                                background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #F26A2E 0%, #DC5829 100%)',
                                },
                            }}
                        >
                            New Category
                        </Button>
                        )}
                    </Box>
                </Box>
            )}

            {/* Search + all filters in a single flex row that wraps to a second line only
                when the viewport is too narrow (no horizontal scroll). The shortened "All"
                labels keep each pill tight so the row fits on a normal-width screen. The row
                has no left inset, so the search field lines up flush under the tab labels. */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search by parent SKU, bundle SKU, or child SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: '0 0 auto',
                        minWidth: 200,
                        maxWidth: 320,
                        '& .MuiOutlinedInput-root': {
                            height: 40,
                            bgcolor: '#ffffff',
                            '& fieldset': {
                                borderColor: '#ECECE4',
                            },
                            '&:hover fieldset': {
                                borderColor: '#DC5829',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: '#1A1410',
                        },
                    }}
                />

                <FilterDropdown
                    label="Status"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    allLabel="All"
                    minWidth={90}
                    options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                        { value: 'future', label: 'Has Future Version' },
                    ]}
                />

                <FilterDropdown
                    label="Sale Tag"
                    value={saleTagFilter}
                    onChange={setSaleTagFilter}
                    allLabel="All"
                    minWidth={90}
                    options={[
                        { value: 'Live', label: 'Live' },
                        { value: 'Sunset', label: 'Sunset' },
                    ]}
                />

                <FilterDropdown
                    label="Brand"
                    value={brandFilter}
                    onChange={setBrandFilter}
                    allLabel="All"
                    minWidth={90}
                    options={[
                        ...filterOptions.brands.map(brand => ({ value: brand, label: brand })),
                        { value: 'BLANK', label: 'Blank' },
                    ]}
                />

                <FilterDropdown
                    label="Category"
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    allLabel="All"
                    minWidth={90}
                    options={filterOptions.categories.map(cat => ({ value: cat, label: cat }))}
                />

                <FilterDropdown
                    label="Sub-Category"
                    value={subCategoryFilter}
                    onChange={setSubCategoryFilter}
                    allLabel="All"
                    minWidth={90}
                    options={filterOptions.subCategories.map(sub => ({ value: sub, label: sub }))}
                />

                <FilterDropdown
                    label="Tags"
                    value={tagFilter}
                    onChange={setTagFilter}
                    allLabel="All"
                    minWidth={90}
                    options={filterOptions.tags.map(tag => ({ value: tag, label: tag }))}
                />

                <FilterDropdown
                    label="Sale Type"
                    value={saleTypeFilter}
                    onChange={setSaleTypeFilter}
                    allLabel="All"
                    minWidth={90}
                    options={[
                        { value: 'Simple', label: 'Simple' },
                        { value: 'Bundle', label: 'Bundle' },
                    ]}
                />

                <CogsRangeFilter value={cogsFilterValue} onChange={setCogsFilter} />

                <FilterDropdown
                    label="Sort by"
                    value={sortBy}
                    onChange={setSortBy}
                    minWidth={110}
                    options={[
                        { value: 'name-asc', label: 'Name (A-Z)' },
                        { value: 'name-desc', label: 'Name (Z-A)' },
                        { value: 'inventory-high', label: 'Inventory (High to Low)' },
                        { value: 'inventory-low', label: 'Inventory (Low to High)' },
                        { value: 'date-newest', label: 'Date (Newest First)' },
                        { value: 'date-oldest', label: 'Date (Oldest First)' },
                        { value: 'drr-high', label: 'DRR (High to Low)' },
                        { value: 'drr-low', label: 'DRR (Low to High)' },
                        { value: 'doc-high', label: 'DOC (High to Low)' },
                        { value: 'doc-low', label: 'DOC (Low to High)' },
                        { value: 'cogs-asc', label: 'COGS (Low to High)' },
                        { value: 'cogs-desc', label: 'COGS (High to Low)' },
                    ]}
                />

                {activeFilterCount > 0 && (
                    <Button
                        size="small"
                        onClick={clearAllFilters}
                        sx={{ height: 40, textTransform: 'none', whiteSpace: 'nowrap', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                        Clear All
                    </Button>
                )}
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={40} />
                </Box>
            )}

            {/* Empty State */}
            {!loading && categories.length === 0 && (
                <Card
                    sx={{
                        textAlign: 'center',
                        py: 8,
                        background: '#ffffff',
                        border: '1px solid #ECECE4',
                    }}
                >
                    <CardContent>
                        <Category sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Bundle Categories Yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                            Create your first bundle category to start managing bundle SKUs
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setShowCreateDialog(true)}
                        >
                            Create First Category
                        </Button>
                    </CardContent>
                </Card>
            )}

            {canEditSaleTag && !loading && categories.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Checkbox
                        size="small"
                        checked={selectedOnPage > 0 && selectedOnPage === pageParentSKUs.length}
                        indeterminate={selectedOnPage > 0 && selectedOnPage < pageParentSKUs.length}
                        onChange={(e) => setSelectedParents(prev => {
                            const next = new Set(prev);
                            pageParentSKUs.forEach(p => (e.target.checked ? next.add(p) : next.delete(p)));
                            return next;
                        })}
                        inputProps={{ 'aria-label': 'Select all parents on this page' }}
                        sx={{ p: 0.5, color: 'rgba(26, 20, 15, 0.4)', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#DC5829' } }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Select all on this page to change their Sale Tag together
                    </Typography>
                </Box>
            )}

            {/* Categories Grid */}
            {!loading && categories.length > 0 && filteredAndSortedCategories.length > 0 && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)'
                    },
                    gap: 2
                }}>
                    {filteredAndSortedCategories.map((category, index) => (
                        <BundleCategoryCard
                            key={category.parentSKU}
                            category={category}
                            inventoryMap={inventoryMap}
                            canBeMadeMap={canBeMadeMap}
                            onClick={() => handleCategoryClick(category.parentSKU)}
                            onEdit={(e) => handleEditCategory(e, category)}
                            formatDate={formatDate}
                            index={index}
                            canEditSaleTag={canEditSaleTag}
                            saleTagPending={saleTagPending.has(category.parentSKU)}
                            onSaleTagChange={(parentSKU, next) => changeSaleTag([parentSKU], next)}
                            selected={selectedParents.has(category.parentSKU)}
                            onSelectChange={toggleParentSelected}
                        />
                    ))}
                </Box>
            )}

            {selectedParents.size > 0 && <Box aria-hidden sx={{ height: 84 }} />}

            {/* Bulk Sale Tag bar — slides up once at least one parent is selected */}
            <AnimatePresence>
                {canEditSaleTag && selectedParents.size > 0 && (
                    <motion.div
                        role="region"
                        aria-label="Bulk Sale Tag actions"
                        initial={reduceMotion ? { opacity: 0 } : { y: 72, opacity: 0 }}
                        animate={reduceMotion ? { opacity: 1 } : { y: 0, opacity: 1 }}
                        exit={reduceMotion ? { opacity: 0 } : { y: 72, opacity: 0 }}
                        transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 36 }}
                        style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', zIndex: 1200, display: 'flex', justifyContent: 'center', pointerEvents: 'none', padding: '0 16px' }}
                    >
                        <Box
                            sx={{
                                pointerEvents: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                gap: 1.25,
                                pl: 1.5,
                                pr: 1,
                                py: 0.9,
                                borderRadius: '999px',
                                bgcolor: 'rgba(26, 20, 15, 0.94)',
                                border: '1px solid rgba(241, 242, 218, 0.14)',
                                backdropFilter: 'blur(10px)',
                                boxShadow: '0 18px 44px rgba(26, 20, 15, 0.38)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} aria-live="polite">
                                <Box sx={{ minWidth: 28, height: 28, px: 0.8, borderRadius: '999px', bgcolor: '#DC5829', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums' }}>
                                    {selectedParents.size}
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#F1F2DA' }}>
                                    parent{selectedParents.size === 1 ? '' : 's'} selected
                                </Typography>
                            </Box>

                            <Box sx={{ width: '1px', height: 22, bgcolor: 'rgba(241, 242, 218, 0.18)', display: { xs: 'none', sm: 'block' } }} />

                            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(241, 242, 218, 0.65)', fontWeight: 600 }}>
                                Set Sale Tag
                            </Typography>
                            <Button
                                disableElevation
                                onClick={() => changeSaleTag(Array.from(selectedParents), 'Live')}
                                startIcon={<FiberManualRecord sx={{ fontSize: '10px !important' }} />}
                                sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, px: 1.75, borderRadius: '999px', color: '#86efac', backgroundColor: 'rgba(34, 197, 94, 0.16)', border: '1px solid rgba(34, 197, 94, 0.35)', '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.28)' }, '&:focus-visible': { outline: '2px solid #F1F2DA', outlineOffset: 2 } }}
                            >
                                Live
                            </Button>
                            <Button
                                disableElevation
                                onClick={() => changeSaleTag(Array.from(selectedParents), 'Sunset')}
                                startIcon={<WbTwilight sx={{ fontSize: '16px !important' }} />}
                                sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, px: 1.75, borderRadius: '999px', color: '#fca5a5', backgroundColor: 'rgba(239, 68, 68, 0.16)', border: '1px solid rgba(239, 68, 68, 0.38)', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.28)' }, '&:focus-visible': { outline: '2px solid #F1F2DA', outlineOffset: 2 } }}
                            >
                                Sunset
                            </Button>

                            <Tooltip title="Clear selection">
                                <IconButton
                                    aria-label="Clear selection"
                                    onClick={() => setSelectedParents(new Set())}
                                    sx={{ width: 36, height: 36, color: 'rgba(241, 242, 218, 0.7)', '&:hover': { color: '#F1F2DA', bgcolor: 'rgba(241, 242, 218, 0.1)' }, '&:focus-visible': { outline: '2px solid #F1F2DA' } }}
                                >
                                    <Close sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Pagination — server-paged; page state is 0-indexed (api.ts converts to 1-indexed
                internally), so TablePaginationBar's 0-indexed page/onPageChange wire straight through. */}
            {!loading && totalCount > pageSize && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6 }}>
                    <TablePaginationBar
                        page={page}
                        total={totalCount}
                        rowsPerPage={pageSize}
                        onPageChange={(next: number) => {
                            setPage(next);
                            // Scroll the fixed-shell content container (not the window): under the
                            // fixed app shell only <main id="app-main-scroll"> scrolls, so a window
                            // scroll would be a no-op.
                            document.getElementById('app-main-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    />
                </Box>
            )}

            {/* No Search Results */}
            {!loading && filteredAndSortedCategories.length === 0 && (
                <Card
                    sx={{
                        textAlign: 'center',
                        py: 6,
                        background: '#ffffff',
                        border: '1px solid #ECECE4',
                    }}
                >
                    <CardContent>
                        <Search sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No bundles found
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                            {searchQuery
                                ? <>No bundles match "<strong>{searchQuery}</strong>". Try a different search term.</>
                                : "No bundles match your current filters or there are no categories yet."}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Create Dialog */}
            <CreateBundleCategory
                open={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                onSuccess={handleCreateSuccess}
            />

            {/* Edit Dialog */}
            <Dialog open={!!editingCategory} onClose={() => setEditingCategory(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Bundle</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Bundle Name (Parent SKU)"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value.toUpperCase())}
                            helperText="Changing the name will update all versions of this bundle"
                        />

                        {/* Bundle Details Section */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
                            Bundle Details
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Category Code"
                                placeholder="e.g., COMBO-001"
                                value={editCategoryCode}
                                onChange={(e) => setEditCategoryCode(e.target.value.toUpperCase())}
                            />
                            <TextField
                                fullWidth
                                label="Bundle Name"
                                placeholder="e.g., Diwali Gift Box"
                                value={editBundleName}
                                onChange={(e) => setEditBundleName(e.target.value)}
                            />
                        </Box>

                        <TextField
                            fullWidth
                            select
                            label="Type"
                            value={editType}
                            onChange={(e) => setEditType(e.target.value)}
                        >
                            <MenuItem value="SIMPLE">SIMPLE</MenuItem>
                            <MenuItem value="BUNDLE">BUNDLE</MenuItem>
                        </TextField>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                select
                                label="Sale Type *"
                                value={editSaleType}
                                onChange={(e) => setEditSaleType(e.target.value)}
                                required
                            >
                                <MenuItem value="Simple">Simple</MenuItem>
                                <MenuItem value="Bundle">Bundle</MenuItem>
                            </TextField>
                        </Box>

                        {/* Pricing Section */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
                            Pricing
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Max Retail Price (MRP)"
                                placeholder="e.g., 999.00"
                                value={editMaxRetailPrice}
                                onChange={(e) => setEditMaxRetailPrice(e.target.value)}
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Base Price"
                                placeholder="e.g., 799.00"
                                value={editBasePrice}
                                onChange={(e) => setEditBasePrice(e.target.value)}
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Cost Price"
                                placeholder="e.g., 362.88"
                                value={editCostPrice}
                                onChange={(e) => setEditCostPrice(e.target.value)}
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                            <TextField
                                fullWidth
                                label="GST Tax Type Code"
                                placeholder="e.g., 3"
                                value={editGstTaxTypeCode}
                                onChange={(e) => setEditGstTaxTypeCode(e.target.value)}
                            />
                        </Box>

                        {/* Additional Information Section */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
                            Additional Information (Optional)
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Brand"
                                placeholder="e.g., Salty"
                                value={editBrand}
                                onChange={(e) => setEditBrand(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                label="Category"
                                placeholder="e.g., Gifts"
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value)}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Sub Category"
                                placeholder="e.g., Festival"
                                value={editSubCategory}
                                onChange={(e) => setEditSubCategory(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                label="Handle"
                                placeholder="e.g., diwali-gift-box"
                                value={editHandle}
                                onChange={(e) => setEditHandle(e.target.value)}
                                helperText="URL-friendly identifier"
                            />
                        </Box>

                        {/* Tags Section */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                                Tags (Optional)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Enter tag and press Enter"
                                    value={editTagInput}
                                    onChange={(e) => setEditTagInput(e.target.value)}
                                    onKeyPress={handleEditTagKeyPress}
                                    disabled={editLoading}
                                    size="small"
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleAddEditTag}
                                    disabled={!editTagInput.trim() || editLoading}
                                >
                                    <Add />
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {editTags.map((tag, index) => (
                                    <Chip
                                        key={`${tag}-${index}`}
                                        label={tag}
                                        onDelete={() => handleRemoveEditTag(index)}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Box>

                        {/* Plytix ID Section */}
                        <TextField
                            fullWidth
                            label="Plytix ID (Optional)"
                            value={editPlytixId}
                            onChange={(e) => setEditPlytixId(e.target.value)}
                            placeholder="Enter Plytix ID"
                            disabled={editLoading}
                        />

                        {/* Image Section */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
                            Image (Optional)
                        </Typography>

                        <TextField
                            fullWidth
                            label="Image URL"
                            value={editImageURL}
                            onChange={(e) => setEditImageURL(e.target.value)}
                            placeholder="https://example.com/image.jpg"
                        />
                        {editImageURL && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">Preview:</Typography>
                                <Box
                                    component="img"
                                    src={editImageURL}
                                    alt="Preview"
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 1,
                                        objectFit: 'cover',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                    onError={(e: any) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingCategory(null)} disabled={editLoading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveEdit}
                        disabled={editLoading || !editName.trim()}
                    >
                        {editLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Upload Dialog */}
            <Dialog
                open={showBulkUploadDialog}
                onClose={() => { setShowBulkUploadDialog(false); setBulkUploadResults(null); setAdvancedUploadMode(false); }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Bulk Upload Bundle Categories</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Mode Toggle */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant={!advancedUploadMode ? 'contained' : 'outlined'}
                                onClick={() => { setAdvancedUploadMode(false); setAdvancedSyncMode(false); setBulkUploadResults(null); }}
                                size="small"
                                sx={{ borderRadius: 2 }}
                            >
                                Regular Upload
                            </Button>
                            <Button
                                variant={advancedUploadMode && !advancedSyncMode ? 'contained' : 'outlined'}
                                onClick={() => { setAdvancedUploadMode(true); setAdvancedSyncMode(false); setBulkUploadResults(null); }}
                                size="small"
                                color="warning"
                                sx={{ borderRadius: 2 }}
                            >
                                Advanced (Backfill)
                            </Button>
                            <Button
                                variant={advancedUploadMode && advancedSyncMode ? 'contained' : 'outlined'}
                                onClick={() => { setAdvancedUploadMode(true); setAdvancedSyncMode(true); setBulkUploadResults(null); }}
                                size="small"
                                color="primary"
                                sx={{ borderRadius: 2 }}
                            >
                                Advanced (Unicommerce)
                            </Button>
                        </Box>

                        {/* Instructions */}
                        {!advancedUploadMode ? (
                            <Alert severity="info">
                                <Typography variant="subtitle2" gutterBottom>Regular Upload:</Typography>
                                <Typography variant="body2">
                                    Creates new bundles or new versions automatically. Syncs with Unicommerce. Columns: parentSKU, childs, categoryCode, name, type, maxRetailPrice, basePrice, imageURL
                                </Typography>
                            </Alert>
                        ) : advancedSyncMode ? (
                            <Alert severity="success">
                                <Typography variant="subtitle2" gutterBottom>Advanced (Unicommerce) Sync:</Typography>
                                <Typography variant="body2">
                                    Direct insertion AND **syncs with Unicommerce**. Use for urgent manual overrides. Columns: parentSKU, bundleSku, versionNumber, childs, isActive, categoryCode, name, type, maxRetailPrice, basePrice, imageURL
                                </Typography>
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)' }}>
                                <Typography variant="subtitle2" gutterBottom>Advanced Backfill (No Sync):</Typography>
                                <Typography variant="body2">
                                    Direct insertion **without Unicommerce sync**. Use for historical data migration only. Columns: parentSKU, bundleSku, versionNumber, childs, isActive, categoryCode, name, type, maxRetailPrice, basePrice, imageURL
                                </Typography>
                            </Alert>
                        )}

                        {/* Download Template */}
                        <Box>
                            <Button
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={advancedUploadMode ? handleDownloadAdvancedTemplate : handleDownloadTemplate}
                                sx={{ borderColor: '#16A34A', color: '#16A34A' }}
                            >
                                Download {advancedUploadMode ? 'Advanced' : 'CSV'} Template
                            </Button>
                        </Box>

                        {/* Upload Section */}
                        <Box sx={{
                            p: 3,
                            border: '2px dashed',
                            borderColor: advancedUploadMode ? (advancedSyncMode ? 'primary.main' : 'warning.main') : 'divider',
                            borderRadius: 2,
                            textAlign: 'center',
                            bgcolor: 'rgba(30, 41, 59, 0.3)',
                        }}>
                            {!advancedUploadMode ? (
                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Standard upload format with automatic versioning
                                </Typography>
                            ) : advancedSyncMode ? (
                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Advanced format with manual control **AND Unicommerce sync**
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Advanced format with manual control **(No Sync)**
                                </Typography>
                            )}
                            {!advancedUploadMode ? (
                                <>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                        id="bulk-upload-input"
                                    />
                                    <label htmlFor="bulk-upload-input">
                                        <Button
                                            variant="contained"
                                            component="span"
                                            startIcon={bulkUploadLoading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                                            disabled={bulkUploadLoading}
                                            sx={{
                                                background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #b8451e 0%, #DC5829 100%)',
                                                },
                                            }}
                                        >
                                            {bulkUploadLoading ? 'Uploading...' : 'Select CSV File'}
                                        </Button>
                                    </label>
                                </>
                            ) : (
                                <>
                                    <input
                                        ref={advancedFileInputRef}
                                        type="file"
                                        accept=".csv"
                                        onChange={handleAdvancedFileUpload}
                                        style={{ display: 'none' }}
                                        id="advanced-upload-input"
                                    />
                                    <label htmlFor="advanced-upload-input">
                                        <Button
                                            variant="contained"
                                            component="span"
                                            color="warning"
                                            startIcon={bulkUploadLoading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                                            disabled={bulkUploadLoading}
                                        >
                                            {bulkUploadLoading ? 'Uploading...' : advancedSyncMode ? 'Select Unicommerce CSV' : 'Select Advanced CSV'}
                                        </Button>
                                    </label>
                                </>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Accepts .csv files only
                            </Typography>
                        </Box>

                        {/* Results */}
                        {bulkUploadResults && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Created (Regular mode) */}
                                {bulkUploadResults.created && bulkUploadResults.created.length > 0 && (
                                    <Alert severity="success">
                                        <Typography variant="subtitle2">Created ({bulkUploadResults.created.length}):</Typography>
                                        <Typography variant="body2">
                                            {bulkUploadResults.created.map(b => b.bundleSku).join(', ')}
                                        </Typography>
                                    </Alert>
                                )}

                                {/* New Versions (Regular mode) */}
                                {bulkUploadResults.newVersions && bulkUploadResults.newVersions.length > 0 && (
                                    <Alert severity="info">
                                        <Typography variant="subtitle2">New Versions ({bulkUploadResults.newVersions.length}):</Typography>
                                        <Typography variant="body2">
                                            {bulkUploadResults.newVersions.map(b => `${b.bundleSku} (v${b.version})`).join(', ')}
                                        </Typography>
                                    </Alert>
                                )}

                                {/* Inserted (Advanced mode) */}
                                {bulkUploadResults.inserted && bulkUploadResults.inserted.length > 0 && (
                                    <Alert severity="success">
                                        <Typography variant="subtitle2">Inserted ({bulkUploadResults.inserted.length}):</Typography>
                                        <Typography variant="body2">
                                            {bulkUploadResults.inserted.map(b => `${b.bundleSku} (v${b.versionNumber}${b.isActive ? ', active' : ''})`).join(', ')}
                                        </Typography>
                                    </Alert>
                                )}

                                {/* Skipped (Advanced mode) */}
                                {bulkUploadResults.skipped && bulkUploadResults.skipped.length > 0 && (
                                    <Alert severity="warning">
                                        <Typography variant="subtitle2">Skipped ({bulkUploadResults.skipped.length}):</Typography>
                                        {bulkUploadResults.skipped.map((s, idx) => (
                                            <Typography key={idx} variant="body2">
                                                {s.bundleSku}: {s.reason}
                                            </Typography>
                                        ))}
                                    </Alert>
                                )}

                                {/* Errors */}
                                {bulkUploadResults.errors.length > 0 && (
                                    <Alert severity="error">
                                        <Typography variant="subtitle2">Errors ({bulkUploadResults.errors.length}):</Typography>
                                        {bulkUploadResults.errors.map((err, idx) => (
                                            <Typography key={idx} variant="body2">
                                                Row {err.row}: {err.parentSKU && `${err.parentSKU} - `}{err.error}
                                            </Typography>
                                        ))}
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => { setShowBulkUploadDialog(false); setBulkUploadResults(null); }}
                        disabled={bulkUploadLoading}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </Box >
    );
});

BundleCategoryManager.displayName = 'BundleCategoryManager';

export default BundleCategoryManager;
